import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faShare,
  faBookmark,
  faClock,
  faFire,
  faWineGlassAlt,
  faBolt,
  faStar,
  faStarHalfAlt,
  faLemon,
  faWineBottle,
  faTimes,
  faPlus,
  faUserCircle,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';

const iconMap = {
  faLemon: faLemon,
  faWineBottle: faWineBottle,
  faTimes: faTimes,
};

const characteristicMap = {
  'Sweet': 1,
  'Strong': 2,
  'Hot': 3,
  'Refreshing': 4,
  'Balanced': 5,
  'Sour': 6,
  'Bitter': 7,
  'Fruity': 8,
  'Herbal': 9
};

const IMAGE_BASE_URL = '/img/cocktails/';
const USER_IMAGE_BASE_URL = '/img/users/';
const REVIEW_IMAGE_BASE_URL = '/';

function CocktailDetailPage({ userData, apiClient, onLogout }) {
  const { drinkid } = useParams();
  const navigate = useNavigate();
  const [cocktail, setCocktail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('ingredients');
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [reviewPhoto, setReviewPhoto] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [userReview, setUserReview] = useState(null);
  const fileInputRef = useRef(null);

  const availableTags = ['Sweet', 'Strong', 'Hot', 'Refreshing', 'Balanced', 'Sour', 'Bitter', 'Fruity', 'Herbal'];

  useEffect(() => {
    const fetchCocktail = async () => {
      try {
        const data = await apiClient.request(`/api/cocktail/${drinkid}`, {
          method: 'GET',
        });
        console.log('Cocktail API response:', data);
        setCocktail(data);
        if (data.reviews && userData?.userid) {
          const existingReview = data.reviews.find(review => review.userid === userData.userid);
          console.log('User review:', existingReview);
          setUserReview(existingReview || null);
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch cocktail details');
        if (err.message === 'Failed to refresh token') {
          onLogout();
          navigate('/');
        }
      } finally {
        setLoading(false);
      }
    };

    if (userData && userData.userid && userData.access_token) {
      fetchCocktail();
    } else {
      setError('User data is incomplete');
      setLoading(false);
    }
  }, [drinkid, userData, apiClient, onLogout, navigate]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    if (isReviewModalOpen && userReview) {
      setReviewRating(parseInt(userReview.rating) || 0);
      setReviewComment(userReview.comments || '');
      const reviewCharacteristics = Array.isArray(userReview.characteristics) ? userReview.characteristics : [];
      // Check if characteristics are names (strings) or IDs (numbers)
      const areCharacteristicsNames = reviewCharacteristics.length > 0 && typeof reviewCharacteristics[0] === 'string';
      const selected = areCharacteristicsNames
        ? reviewCharacteristics.filter(tag => availableTags.includes(tag)) // Use names directly
        : Object.keys(characteristicMap).filter(tag =>
            reviewCharacteristics.includes(characteristicMap[tag])
          );
      console.log('User review characteristics:', reviewCharacteristics);
      console.log('Selected tags for edit modal:', selected);
      setSelectedTags(selected);
      setReviewPhoto(null);
      setRemoveImage(false);
      setPreviewUrl(userReview.images && userReview.images.length > 0 ? `${REVIEW_IMAGE_BASE_URL}${userReview.images[0]}` : null);
    } else {
      setReviewRating(0);
      setReviewComment('');
      setSelectedTags([]);
      setReviewPhoto(null);
      setRemoveImage(false);
      setPreviewUrl(null);
    }
  }, [isReviewModalOpen, userReview]);

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<FontAwesomeIcon key={i} icon={faStar} className="text-yellow-400" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<FontAwesomeIcon key={i} icon={faStarHalfAlt} className="text-yellow-400" />);
      } else {
        stars.push(<FontAwesomeIcon key={i} icon={faStarRegular} className="text-yellow-400" />);
      }
    }
    return stars;
  };

  const renderSingleStar = (numStars) => {
    return <FontAwesomeIcon icon={faStar} className="text-xs text-yellow-400" />;
  };

  const renderRatingStars = (currentRating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FontAwesomeIcon
          key={i}
          icon={i <= currentRating ? faStar : faStarRegular}
          className={`rating-star text-2xl ${i <= currentRating ? 'text-gold' : 'text-gray-400'}`}
          onClick={() => setReviewRating(i)}
        />
      );
    }
    return stars;
  };

  const calculateTimeAgo = (dateString) => {
    const reviewDate = new Date(dateString);
    const currentDate = new Date('2025-04-10');
    const diffTime = Math.abs(currentDate - reviewDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffWeeks === 1) return '1 week ago';
    if (diffWeeks < 4) return `${diffWeeks} weeks ago`;
    if (diffMonths === 1) return '1 month ago';
    return `${diffMonths} months ago`;
  };

  const getRatingsDistributionPercentages = () => {
    const totalRatings = parseInt(cocktail.num_ratings) || 0;
    if (totalRatings === 0) {
      return { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    }

    const distribution = cocktail.ratings_distribution || { "5": 0, "4": 0, "3": 0, "2": 0, "1": 0 };
    return {
      5: Math.round((distribution["5"] / totalRatings) * 100),
      4: Math.round((distribution["4"] / totalRatings) * 100),
      3: Math.round((distribution["3"] / totalRatings) * 100),
      2: Math.round((distribution["2"] / totalRatings) * 100),
      1: Math.round((distribution["1"] / totalRatings) * 100),
    };
  };

  const handleTagToggle = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setReviewPhoto(file);
      setRemoveImage(false); // Reset remove state when uploading a new photo
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleAddPhotoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveImageClick = () => {
    setRemoveImage((prev) => !prev); // Toggle remove state
  };

  const handleReviewSubmit = async () => {
    if (reviewRating < 1 || reviewRating > 5) {
      setReviewError('Please select a rating between 1 and 5 stars.');
      return;
    }

    const characteristicIds = selectedTags
      .map(tag => characteristicMap[tag])
      .filter(id => id >= 1 && id <= 9);

    let config;
    if (reviewPhoto) {
      // Use multipart/form-data for new image uploads
      const formData = new FormData();
      formData.append('rating', reviewRating.toString());
      if (reviewComment) {
        formData.append('comments', reviewComment);
      }
      characteristicIds.forEach(id => {
        formData.append('characteristicids[]', id.toString());
      });
      formData.append('images', reviewPhoto);

      console.log('Submitting FormData:');
      for (const [key, value] of formData.entries()) {
        console.log(`${key}: ${value instanceof File ? value.name : value}`);
      }

      config = {
        method: 'POST',
        data: formData,
      };
    } else {
      // Use application/json when no new image
      const payload = {
        rating: reviewRating,
        comments: reviewComment || '',
        characteristicids: characteristicIds,
      };

      console.log('Submitting JSON payload:', payload);

      config = {
        method: 'POST',
        data: payload,
        headers: { 'Content-Type': 'application/json' },
      };
    }

    try {
      console.log('Request config:', {
        url: `/api/reviews/${drinkid}`,
        headers: {
          Authorization: apiClient.accessToken ? `Bearer ${apiClient.accessToken}` : 'No token',
          'Content-Type': reviewPhoto ? 'multipart/form-data (boundary auto-set by FormData)' : 'application/json',
        },
      });

      const response = await apiClient.request(`/api/reviews/${drinkid}`, config);

      console.log('API response:', response);

      const updatedCocktail = await apiClient.request(`/api/cocktail/${drinkid}`, {
        method: 'GET',
      });
      setCocktail(updatedCocktail);
      const existingReview = updatedCocktail.reviews.find(review => review.userid === userData.userid);
      setUserReview(existingReview || null);
      setIsReviewModalOpen(false);
      setReviewRating(0);
      setReviewComment('');
      setSelectedTags([]);
      setReviewPhoto(null);
      setRemoveImage(false);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      setReviewError('');
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data || err.message || 'Failed to submit review';
      console.error('Review submission error:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        headers: err.response?.headers,
        request: {
          url: `/api/reviews/${drinkid}`,
        },
      });
      setReviewError(errorMessage);
      if (err.message === 'Failed to refresh token') {
        onLogout();
        navigate('/');
      }
    }
  };

  const handleReviewUpdate = async () => {
    if (reviewRating < 1 || reviewRating > 5) {
      setReviewError('Please select a rating between 1 and 5 stars.');
      return;
    }

    if (!userReview?.recordid) {
      setReviewError('No existing review found to update.');
      return;
    }

    const characteristicIds = selectedTags
      .map(tag => characteristicMap[tag])
      .filter(id => id >= 1 && id <= 9);

    let config;
    if (reviewPhoto) {
      // Use multipart/form-data for new image uploads
      const formData = new FormData();
      formData.append('rating', reviewRating.toString());
      if (reviewComment) {
        formData.append('comments', reviewComment);
      }
      characteristicIds.forEach(id => {
        formData.append('characteristicids[]', id.toString());
      });
      formData.append('images', reviewPhoto);

      console.log('Updating FormData:');
      for (const [key, value] of formData.entries()) {
        console.log(`${key}: ${value instanceof File ? value.name : value}`);
      }

      config = {
        method: 'PUT',
        data: formData,
      };
    } else {
      // Use application/json when no new image
      const payload = {
        rating: reviewRating,
        comments: reviewComment || '',
        characteristicids: characteristicIds,
      };
      if (removeImage) {
        payload.images = null; // Signal image removal
      }

      console.log('Updating JSON payload:', payload);

      config = {
        method: 'PUT',
        data: payload,
        headers: { 'Content-Type': 'application/json' },
      };
    }

    try {
      console.log('Request config:', {
        url: `/api/reviews/${userReview.recordid}`,
        headers: {
          Authorization: apiClient.accessToken ? `Bearer ${apiClient.accessToken}` : 'No token',
          'Content-Type': reviewPhoto ? 'multipart/form-data (boundary auto-set by FormData)' : 'application/json',
        },
      });

      const response = await apiClient.request(`/api/reviews/${userReview.recordid}`, config);

      console.log('API response:', response);

      const updatedCocktail = await apiClient.request(`/api/cocktail/${drinkid}`, {
        method: 'GET',
      });
      setCocktail(updatedCocktail);
      const existingReview = updatedCocktail.reviews.find(review => review.userid === userData.userid);
      setUserReview(existingReview || null);
      setIsReviewModalOpen(false);
      setReviewRating(0);
      setReviewComment('');
      setSelectedTags([]);
      setReviewPhoto(null);
      setRemoveImage(false);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      setReviewError('');
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data || err.message || 'Failed to update review';
      console.error('Review update error:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        headers: err.response?.headers,
        request: {
          url: `/api/reviews/${userReview.recordid}`,
        },
      });
      setReviewError(errorMessage);
      if (err.message === 'Failed to refresh token') {
        onLogout();
        navigate('/');
      }
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center">{error}</div>;
  }

  const ratingsPercentages = cocktail ? getRatingsDistributionPercentages() : { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

  return (
    <div className="cocktail-detail-container">
      <div className="cocktail-image-wrapper">
        <img
          src={`${IMAGE_BASE_URL}${cocktail.picture}`}
          alt={cocktail.name}
          className="cocktail-hero-image"
        />
        <div className="gradient-overlay"></div>
      </div>
      <div className="cocktail-content-wrapper">
        <div className="cocktail-top-buttons">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div className="flex space-x-3">
            <button className="std-40-btn">
              <FontAwesomeIcon icon={faShare} className="text-white" />
            </button>
            <button className="std-40-btn">
              <FontAwesomeIcon icon={faBookmark} className="text-primary" />
            </button>
          </div>
        </div>
        <div className="cocktail-main-content">
          <div className="cocktail-detail-top-container">
            <div className="flex justify-between items-start mb-2">
              <h1 className="cocktail-detail-name">{cocktail.name}</h1>
              <span className="cocktail-base-liquor-pill">{cocktail.base_liquor}</span>
            </div>
            <div className="flex items-center mb-4">
              <div className="flex text-gold-stars text-sm mr-2">
                {renderStars(parseFloat(cocktail.rating))}
              </div>
              <span className="text-sm text-gray-300">
                {cocktail.rating} ({cocktail.num_ratings} reviews)
              </span>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">{cocktail.description}</p>
          </div>
          <div className="cocktail-detail-info-container">
            <div className="text-center">
              <div className="std-48-circle-blue mb-1">
                <FontAwesomeIcon icon={faClock} className="text-primary" />
              </div>
              <span className="text-xs text-gray-300">Prep time</span>
              <p className="cocktail-info-values">{cocktail.prep_time} min</p>
            </div>
            <div className="text-center">
              <div className="std-48-circle-blue mb-1">
                <FontAwesomeIcon icon={faFire} className="text-primary" />
              </div>
              <span className="text-xs text-gray-300">Difficulty</span>
              <p className="cocktail-info-values">{cocktail.difficulty}</p>
            </div>
            <div className="text-center">
              <div className="std-48-circle-blue mb-1">
                <FontAwesomeIcon icon={faWineGlassAlt} className="text-primary" />
              </div>
              <span className="text-xs text-gray-300">Serves</span>
              <p className="cocktail-info-values">{cocktail.serves}</p>
            </div>
            <div className="text-center">
              <div className="std-48-circle-blue mb-1">
                <FontAwesomeIcon icon={faBolt} className="text-primary" />
              </div>
              <span className="text-xs text-gray-300">Calories</span>
              <p className="cocktail-info-values">{cocktail.calories}</p>
            </div>
          </div>
          <div className="cocktail-detail-buttons-ing-ins-rev">
            <div className="flex border-b border-gray-700">
              <button
                className={`tab-item ${activeTab === 'ingredients' ? 'active' : 'text-gray-400'}`}
                onClick={() => setActiveTab('ingredients')}
              >
                Ingredients
              </button>
              <button
                className={`tab-item ${activeTab === 'instructions' ? 'active' : 'text-gray-400'}`}
                onClick={() => setActiveTab('instructions')}
              >
                Instructions
              </button>
              <button
                className={`tab-item ${activeTab === 'reviews' ? 'active' : 'text-gray-400'}`}
                onClick={() => setActiveTab('reviews')}
              >
                Reviews
              </button>
            </div>
          </div>
          {activeTab === 'ingredients' && (
            <div className="cocktail-detail-ingredients-container">
              <div className="cocktail-detail-ingredients-header">
                <h2 className="text-xl">Ingredients</h2>
                <button className="new-list-btn flex items-center">
                  <FontAwesomeIcon icon={faPlus} className="mr-1 text-primary" />
                  Add to shopping list
                </button>
              </div>
              <ul className="cocktail-detail-ingredients-list">
                {cocktail.ingredients.map((ingredient, index) => (
                  <li key={index} className="ingredient-row">
                    <div className="flex items-center">
                      <div className="ingredients-icon">
                        <FontAwesomeIcon
                          icon={iconMap[ingredient.icon]}
                          className={ingredient.icon_color}
                        />
                      </div>
                      <span>{ingredient.name}</span>
                    </div>
                    <span className="text-gray-300">
                      {ingredient.amount} {ingredient.unit}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {activeTab === 'instructions' && (
            <div className="cocktail-detail-related-container">
              <h2 className="text-xl font-semibold mb-4">Instructions</h2>
              <p className="text-gray-300">{cocktail.instructions}</p>
            </div>
          )}
          {activeTab === 'reviews' && (
            <div className="cocktail-detail-related-container">
              <div className="flex items-stretch mb-6">
                <div className="cocktail-detail-reviews-average-summary">
                  <div className="text-4xl font-bold text-gold-stars mb-1">{cocktail.rating}</div>
                  <div className="flex text-gold-stars text-sm mb-1">
                    {renderStars(parseFloat(cocktail.rating))}
                  </div>
                  <p className="text-xs text-gray-400">{cocktail.num_ratings} reviews</p>
                </div>
                <div className="cocktail-detail-reviews-distribution-container">
                  {[5, 4, 3, 2, 1].map((star) => (
                    <div key={star} className="flex items-center mb-2">
                      <div className="flex items-center w-20 text-gold-stars">
                        <span className="text-xs mr-1 text-gray-400">{star}</span>
                        {renderSingleStar(star)}
                      </div>
                      <div className="flex-1 bg-dark-700 h-2 rounded-full overflow-hidden">
                        <div
                          className="bg-yellow-400 h-full rounded-full"
                          style={{ width: `${ratingsPercentages[star]}%` }}
                        ></div>
                      </div>
                      <span className="text-xs ml-2 w-8 text-right">{ratingsPercentages[star]}%</span>
                    </div>
                  ))}
                </div>
              </div>
              <button
                className="cocktail-detail-reviews-submit-btn"
                onClick={() => setIsReviewModalOpen(true)}
              >
                {userReview ? 'Edit my Review' : 'Write a Review'}
              </button>
              <div className="space-y-5">
                {cocktail.reviews && cocktail.reviews.length > 0 ? (
                  cocktail.reviews.map((review, index) => (
                    <div key={`${review.userid}-${review.drinkid}`} className="review-card p-4 rounded-xl">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
                            {review.picture ? (
                              <img
                                src={`${USER_IMAGE_BASE_URL}${review.picture}`}
                                alt={review.alias}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                            ) : (
                              <FontAwesomeIcon
                                icon={faUserCircle}
                                className="w-full h-full text-gray-400"
                              />
                            )}
                          </div>
                          <div>
                            <h4 className="font-medium">{review.alias}</h4>
                            <div className="flex text-gold-stars text-xs">
                              {renderStars(parseInt(review.rating))}
                            </div>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">
                          {calculateTimeAgo(review.createddate)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-300 leading-relaxed">{review.comments}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-300">No reviews yet. Be the first to write one!</p>
                )}
              </div>
            </div>
          )}
          <div className="cocktail-detail-related-container">
            <h2 className="text-xl font-semibold mb-4">You might also like</h2>
            <div className="flex overflow-x-auto pb-4">
              {cocktail.similar_cocktails.map((similar, index) => (
                <div
                  key={index}
                  className="card cocktail-related-recipe-card flex-shrink-0 w-36 rounded-xl overflow-hidden"
                  onClick={() => navigate(`/cocktail/${similar.drinkid}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="h-36 overflow-hidden">
                    <img
                      src={`${IMAGE_BASE_URL}${similar.picture}`}
                      alt={similar.name}
                      className="w-full h-full object-cover cocktail-related-img"
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="cocktail-related-title">{similar.name}</h3>
                    <div className="flex items-center mt-1">
                      <div className="flex text-gold-stars text-xs">
                        {renderStars(parseFloat(similar.rating))}
                      </div>
                      <span className="text-xs text-gray-400 ml-1">{similar.rating}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {isReviewModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center">
          <div className="card absolute top-1/2 left-1/2 transform w-80 bg-dark-800 rounded-2xl p-6 text-white border-box">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-white close-btn"
              onClick={() => {
                setIsReviewModalOpen(false);
                setReviewRating(0);
                setReviewComment('');
                setSelectedTags([]);
                setReviewPhoto(null);
                setRemoveImage(false);
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(null);
                }
                setReviewError('');
              }}
              aria-label="Close review modal"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h3 className="text-xl font-semibold mb-6 text-center">
              {userReview ? 'Edit Your Review' : 'Write a Review'}
            </h3>
            <div className="mb-6">
              <label className="block text-sm text-gray-300 mb-2">Your Rating</label>
              <div className="flex justify-center space-x-2">
                {renderRatingStars(reviewRating)}
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm text-gray-300 mb-2">Your Review</label>
              <textarea
                className="review-textarea bg-dark-700 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                rows="4"
                placeholder="Share your thoughts about this cocktail..."
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
              ></textarea>
            </div>
            <div className="mb-6">
                <label className="block text-sm text-gray-300 mb-2">{userReview ? 'Update Photo' : 'Add Photo (optional)'}</label>
                <div className="flex space-x-2 pointer">
                    {previewUrl ? (
                    <div className="relative w-16 h-16 min-w-16 border border-red-500">
                        <img
                        src={previewUrl}
                        alt="Selected photo preview"
                        className="w-full h-full rounded-lg border border-dashed border-gray-500 object-cover cursor-pointer"
                        onClick={handleAddPhotoClick}
                        title="Click to select a different photo"
                        onError={(e) => {
                            console.error('Failed to load review image:', previewUrl);
                            e.target.style.display = 'none';
                        }}
                        />
                        <FontAwesomeIcon
                        icon={faTrash}
                        className={`trash-icon ${removeImage ? 'remove' : ''}`}
                        onClick={handleRemoveImageClick}
                        title={removeImage ? 'Undo remove image' : 'Remove image'}
                        />
                    </div>
                    ) : (
                    <button
                        className="w-16 h-16 min-w-16 rounded-lg border border-dashed border-gray-500 flex items-center justify-center text-gray-400 hover:text-primary hover:border-primary transition-colors"
                        onClick={handleAddPhotoClick}
                        type="button"
                        aria-label="Upload a photo"
                    >
                        <FontAwesomeIcon icon={faPlus} className="text-gray-400" />
                    </button>
                    )}
                    <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif"
                    className="hidden"
                    ref={fileInputRef}
                    onChange={handlePhotoChange}
                    />
                </div>
                {reviewPhoto && (
                    <p className="text-sm text-gray-300 mt-2">{reviewPhoto.name}</p>
                )}
                </div>
            <div className="mb-6">
              <p className="block text-sm text-gray-300 mb-2">Characteristics (Optional)</p>
              <div className="flex flex-wrap gap-2">
                {availableTags.map((tag) => (
                  <button
                    key={tag}
                    className={`tag-btn ${selectedTags.includes(tag) ? 'selected' : ''}`}
                    onClick={() => handleTagToggle(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            {reviewError && (
              <p className="text-red-500 text-sm text-center mb-4">{reviewError}</p>
            )}
            <div className="flex justify-center space-x-2">
              <button
                className="cancel-btn"
                onClick={() => {
                  setIsReviewModalOpen(false);
                  setReviewRating(0);
                  setReviewComment('');
                  setSelectedTags([]);
                  setReviewPhoto(null);
                  setRemoveImage(false);
                  if (previewUrl) {
                    URL.revokeObjectURL(previewUrl);
                    setPreviewUrl(null);
                  }
                  setReviewError('');
                }}
              >
                Cancel
              </button>
              <button
                className="create-btn"
                onClick={userReview ? handleReviewUpdate : handleReviewSubmit}
              >
                {userReview ? 'Update' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CocktailDetailPage;